import React from 'react';
import styles from './Layout.module.css';
import classNames from 'classnames';
import { Rhythm, RhythmScale } from '../Theme';

export type LayoutProps = {
  /** Enable an automatic gutter when possible, set an enforced gutter, or disable the gutter and have the items fill the space */
  gutter?: boolean | RhythmScale | number;
  /** Specify how the layout aligns inside its container */
  position?: 'start' | 'middle' | 'end';
  /** Set how `Layout.Item`s align inside the layout, following the direction of flow */
  align?: 'start' | 'middle' | 'end';
  /** EXPERIMENTAL: Specify whether the layout flows horizontal or vertical */
  flow?: 'horizontal' | 'vertical';
  /** Specify where the layout wraps, switching from ltr to rtl, or ttb to btt */
  wrap?: 'start' | 'end';
  /** Specify the size (width for horizontal, height for vertical) of the layout, which all Layout division are calculated from */
  size?: number;
};

export const Layout: React.FC<LayoutProps> = ({
  align = 'middle',
  gutter = true,
  flow = 'horizontal',
  position = 'middle',
  size,
  wrap = 'end',
  children,
}) => {
  const vars: Record<string, string> = {};
  const className = classNames(styles.Layout, {
    [styles.AlignStart]: align === 'start',
    [styles.AlignMiddle]: align === 'middle',
    [styles.AlignEnd]: align === 'end',
    [styles.PositionStart]: position === 'start',
    [styles.PositionMiddle]: position === 'middle',
    [styles.PositionEnd]: position === 'end',
  });

  if (typeof size === 'number' && flow === 'horizontal')
    vars['--layout-width'] = `${size}px`;
  if (typeof size === 'number' && flow === 'vertical')
    vars['--layout-height'] = `${size}px`;
  if (typeof gutter === 'number') vars['--layout-gutter'] = `${gutter}px`;
  if (gutter === 'xs') vars['--layout-gutter'] = Rhythm.xs;
  if (gutter === 'sm') vars['--layout-gutter'] = Rhythm.sm;
  if (gutter === 'md') vars['--layout-gutter'] = Rhythm.md;
  if (gutter === 'lg') vars['--layout-gutter'] = Rhythm.lg;
  if (gutter === 'xl') vars['--layout-gutter'] = Rhythm.xl;
  if (gutter === false) vars['--layout-gap'] = 'space-between';
  if (gutter === true) vars['--layout-gap'] = 'space-around';
  if (flow === 'horizontal') vars['--layout-direction'] = 'row';
  if (wrap === 'start') vars['--layout-wrap'] = 'wrap-reverse';

  return (
    <div className={className} style={vars as React.CSSProperties}>
      {children}
    </div>
  );
};
